"use client";

/**
 * Aviso discreto cuando hay una nueva versión del Service Worker esperando.
 *
 * Se dispara por el evento `cacique:sw-update` (emitido por ServiceWorkerBoot).
 * Muestra una toast-card sobria (mismo estilo del proyecto: bg-surface,
 * border-border) con un botón "Actualizar" que envía SKIP_WAITING al SW
 * y recarga la página.
 *
 * No agrega rutas ni cambia layout.
 */

import { useEffect, useState } from "react";

export default function NewVersionPrompt() {
  const [waitingReg, setWaitingReg] = useState<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (e: Event) => {
      const ce = e as CustomEvent<ServiceWorkerRegistration>;
      if (ce.detail) setWaitingReg(ce.detail);
    };
    window.addEventListener("cacique:sw-update", handler as EventListener);
    return () => window.removeEventListener("cacique:sw-update", handler as EventListener);
  }, []);

  if (!waitingReg) return null;

  const handleUpdate = () => {
    const sw = waitingReg.waiting;
    if (!sw) {
      window.location.reload();
      return;
    }
    sw.postMessage({ type: "SKIP_WAITING" });
    // Cuando el nuevo SW tome control, recarga.
    const onControllerChange = () => {
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed z-[60] bottom-4 left-1/2 -translate-x-1/2 max-w-[calc(100vw-2rem)] rounded-xl border border-border bg-surface px-4 py-3 shadow-xl flex items-center gap-3"
    >
      <div className="text-xs text-text">
        Nueva versión disponible.
      </div>
      <button
        type="button"
        onClick={handleUpdate}
        className="text-xs font-semibold rounded-lg px-3 py-1.5 bg-accent/15 text-accent hover:bg-accent/25 transition"
      >
        Actualizar
      </button>
      <button
        type="button"
        onClick={() => setWaitingReg(null)}
        className="text-xs text-text-muted hover:text-text"
        aria-label="Descartar"
      >
        ✕
      </button>
    </div>
  );
}
